import { useRouter } from 'next/router';
import {
  ClientType,
  type ReferralClient,
} from '@/common/hooks/referrals/types';
import { TXM_LEASES_PATH, TXM_LISTINGS_PATH } from '@/constants';
import { getInteractionButtonLinks } from '@/views/Referrals/utils';
import {
  TrackReferralsEvent,
  useTrackReferralsAnalytics,
} from '$analytics/useTrackAnalytics/domains/referrals';
import { CreateInteractionButton } from './CreateInteractionButton';
import { CreateInteractionButtonDropdown } from './CreateInteractionButtonDropdown';
import { useCreateBuyerAgreementButton } from './CreateInteractionButtonRenderer.helpers';

interface Props {
  clientTypes: ReferralClient['clientTypes'];
  clientTypesList: ReferralClient['clientTypesList'];
  referralId: string;
}

export const CreateInteractionButtonRenderer = ({
  clientTypes,
  clientTypesList,
  referralId,
}: Props) => {
  const trackEvent = useTrackReferralsAnalytics();
  const { query } = useRouter();

  // Determine if we need to present multiple options; if not, we'll render
  // a single link button
  const isSingularClientType = clientTypesList.length === 1;
  const singularClientType = clientTypesList[0];
  const isSingularNonLeaseClientType =
    isSingularClientType && singularClientType !== ClientType.Buyer;
  const isOnlyLeaseClientTypes =
    clientTypesList.length === 2 &&
    clientTypesList.includes(ClientType.Landlord) &&
    clientTypesList.includes(ClientType.Tenant);

  const createBuyerButton = useCreateBuyerAgreementButton(referralId);

  if (isOnlyLeaseClientTypes || isSingularNonLeaseClientType) {
    return (
      <CreateInteractionButton
        buttonText={
          singularClientType === ClientType.Seller
            ? 'Create Listing'
            : 'Create Lease'
        }
        href={
          singularClientType === ClientType.Seller
            ? `${TXM_LISTINGS_PATH}/new?referralId=${referralId}`
            : `${TXM_LEASES_PATH}/new?referralId=${referralId}`
        }
        onClick={() => {
          trackEvent(TrackReferralsEvent.ClickCreateTransactionButton, {
            eventContainer: query.referralId
              ? 'referral_detail_view'
              : 'referral_list_view',
          });
        }}
      />
    );
  }

  const options = [];

  if (clientTypes?.referralBuyer) {
    options.push(createBuyerButton);
  }

  options.push(
    ...getInteractionButtonLinks({
      referralId,
      clientTypes,
    }),
  );

  return <CreateInteractionButtonDropdown menuOptions={options} />;
};
